import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { Agent } from "src/app/models/agent.model";
import { AgentGroup } from "src/app/models/agentGroup.model";
import { Lookup } from "src/app/models/lookup.model";
import { AgentService } from "src/app/services/agent.service";
import { AgentGroupService } from "src/app/services/agentGroup.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
    IOrganizationLevelEntity,
} from "src/app/shared/form/form.component";
import {
    FormField,
    MultiSelectionFormField,
} from "src/app/shared/form/formField.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { AGENT_OPTION_LABEL_MAP } from "src/app/shared/form/employee-field-helpers";

export interface IGroupToAgentDialogData
    extends IFormDialogData, IOrganizationLevelEntity {
    id: string;
    name: string;
    type: number;
    casId: string;
    addedToMySite: boolean;
    agentIds?: string[];
    agentGroupSearchField?: string;
}

@Component({
  standalone: false,
    selector: "app-group-to-agent-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class GroupToAgentDialogComponent
    extends FormComponent<AgentGroup>
    implements OnInit
{
    agentsList: Lookup[] = [];
    filteredAgents: Lookup[] = [];

    constructor(
        dialogRef: MatDialogRef<GroupToAgentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IGroupToAgentDialogData,
        protected agentService: AgentService,
        protected agentGroupService: AgentGroupService,
        protected injector: Injector,
    ) {
        super(dialogRef, agentGroupService, data, "Agent", injector);
        this.title = this.translateService.getDynamicTranslation(
            "Add Agents to Group",
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            agents: this.agentService.getAll(),
        }).subscribe(
            (result) => {
                this.entity = this.data;
                this.agentsList = result.agents;
                this.filteredAgents = [...result.agents];
                this.initFields();
                this.isFormLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        );
    }

    getNewObject(): AgentGroup {
        return new AgentGroup();
    }

    validate(): boolean {
        if (!this.entity.agentIds?.length) {
            this.toastService.displayErrorMessage(
                "Please select at least one agent",
                true,
            );
            return false;
        }
        return true;
    }

    initFields(): void {
        const agentSearchfields = [
            nameOf<Agent>((x) => x.name),
            nameOf<Agent>((x) => x.casId),
        ];

        this.fields = [
            new MultiSelectionFormField({
                property: nameOf((x: AgentGroup) => x.agentIds),
                name: "Select Agents",
                options: this.filteredAgents,
                cssClass: "vertical-checkbox-options",
                columnSpan: new ColumnSpan(2, 2),
                searchKeys: agentSearchfields,
                selectionTemplate: (opt: FormField) =>
                    `${opt.options
                        .filter((res) =>
                            this.entity[opt.property]?.includes(res.id),
                        )
                        .map((res) => res.name)
                        .join(", ")}`,
                optionTemplateFields: agentSearchfields,
                optionTemplateLabelMap: { ...AGENT_OPTION_LABEL_MAP },
            }),
        ];
    }
}
